import { useTranslation } from 'react-i18next';
import type { UseTranslationResponse } from 'react-i18next';

export const ns = 'search';
export const useSearchPageTranslation = (): UseTranslationResponse<typeof ns> => useTranslation(ns);

export const ruRu = (__LOCALE_RU__ ? ({
  [ns]: {
    page: {
      title: 'Поиск',
      hint: {
        choose_search_object: 'Необходимо выбрать объект поиска.',
        choose_list: 'Необходимо выбрать список.',
      },
      error: {
        search_failed: 'Поиск не удался',
      },
    },
    reference_selector: {
      title: 'Искать по',
      search_type_switch: {
        buttons: {
          photo: 'Фото',
          event: 'ID события',
          face: 'ID лица',
        },
      },
      event_id_input: {
        placeholder: 'Введите ID события',
      },
      face_input: {
        title: 'Какой ID вы используете?',
        labels: {
          external_id: 'ID карточки',
          luna_id: 'ID Лица',
        },
        external_id_input: {
          placeholder: 'Введите <ID карточки>',
        },
        face_id_input: {
          placeholder: 'Введите <ID лица>',
        },
        results: {
          faces: {
            title: 'Лица',
          },
          bodies: {
            title: 'Тела',
          },
        },
      },
      image_input: {
        title: 'Обнаружено',
        loader: {
          label: 'Выполняется детекция..',
        },
        info: {
          label: 'Выберите нужное лицо/тело для поиска',
        },
      },
      event_preview: {
        top_section: {
          badge: {
            label: 'Совпадение',
            label_detection: 'Обнаружение',
          },
          similarity_tag: {
            label: 'Карточка {{value}}%',
          },
        },
        lists: 'Списки',
        videostream: {
          removed: 'Видеопоток удален',
          unavailable: 'Видеопоток недоступен',
        },
      },
      face_preview: {
        info: {
          created: 'Создано',
          lists: 'Список',
          face_id: 'ID лица',
          user_data: 'Информация',
        },
      },
      error: {
        no_detections: 'Лица и силуэты тел не обнаружены',
      },
    },
    filters: {
      title: 'Условия поиска',
      buttons: {
        search_button: {
          label: 'Найти',
        },
        reset_button: {
          label: 'Очистить',
        },
        candidate_origin_button_group: {
          faces: 'Ищу лица',
          events: 'Ищу события',
        },
      },
      common: {
        from: 'От',
        to: 'До',
        similarity_percent: 'Схожесть, %',
      },
      face_filters: {
        creation_date: 'Дата создания',
        external_id: 'Внешний ID',
        similarity: 'Схожесть',
        lists: 'Списки',
        user_data: 'Пользовательские данные',
      },
      event_filters: {
        general: {
          title: 'Общие',
          creation_date: 'Дата события',
          video_source: 'Источник',
          handlers: 'Сценарии',
          similarity: 'Схожесть',
        },
        comparing_attributes: {
          title: 'Параметры сравнения',
          comparing_attribute: 'Параметр сравнения',
          top_similar_objects: 'ID объектов с максимальным результатом сравнения',
        },
        face_attributes: {
          title: 'Атрибуты и свойства лица',
          gender: 'Пол',
          age: 'Возраст',
          emotion: 'Эмоция',
          mask: 'Маска',
          liveness: 'Liveness',
          deepfake: 'Deepfake',
          face_ids: 'ID лиц событий',
        },
        body_attributes: {
          title: 'Атрибуты и свойства тела',
          gender: 'Пол',
          age: 'Возраст',
          upper_attributes: 'Верх',
          lower_attributes: 'Низ',
          shoes: 'Обувь',
          head_wear: 'Головной убор',
          backpack: 'Рюкзак',
          upper_colors: 'Цвет верха',
          sleeve: 'Рукав',
          lower_colors: 'Цвет низа',
          lower_type: 'Тип низа',
        },
        location: {
          title: 'Локация',
          tabs: {
            map: 'Карта',
            address: 'Адрес',
            coordinates: 'Координаты',
          },
          address_tab: {
            city: 'Город',
            area: 'Область',
            district: 'Район',
            street: 'Улица',
            house_number: 'Номер дома',
          },
          coordinates_tab: {
            longitude: 'Долгота (-180...180)',
            latitude: 'Широта (-90...90)',
            error: 'Погрешность (0...90)',
          },
        },
      },
    },
    results: {
      title: 'Найдено',
      results_count: {
        label_events_one: '{{count}} событие',
        label_events_few: '{{count}} события',
        label_events_many: '{{count}} событий',
        label_faces_one: '{{count}} лицо',
        label_faces_few: '{{count}} лица',
        label_faces_many: '{{count}} лиц',
      },
      sort_button: {
        label_similarity: 'По схожести',
        label_create_time_asc: 'По дате создания (сначала старые)',
        label_create_time_desc: 'По дате создания (сначала новые)',
      },
      no_data: 'Нет данных',
    },
  },
}) : {}) as Record<string, unknown>;

export const en = (__LOCALE_EN__ ? ({
  [ns]: {
    page: {
      title: 'Search',
      hint: {
        choose_search_object: 'The search object must be selected.',
        choose_list: 'A list must be selected.',
      },
      error: {
        search_failed: 'Search failed',
      },
    },
    reference_selector: {
      title: 'Search by',
      search_type_switch: {
        buttons: {
          photo: 'Photo',
          event: 'Event ID',
          face: 'Face ID',
        },
      },
      event_id_input: {
        placeholder: 'Enter event ID',
      },
      face_input: {
        title: 'Which ID do you use?',
        labels: {
          external_id: 'Card ID',
          luna_id: 'Face ID',
        },
        external_id_input: {
          placeholder: 'Enter <card ID>',
        },
        face_id_input: {
          placeholder: 'Enter <Face ID>',
        },
        results: {
          faces: {
            title: 'Faces',
          },
          bodies: {
            title: 'Bodies',
          },
        },
      },
      image_input: {
        title: 'Detected',
        loader: {
          label: 'Detection in progress..',
        },
        info: {
          label: 'Pick face/body to search for',
        },
      },
      event_preview: {
        top_section: {
          badge: {
            label: 'Match',
            label_detection: 'Detection',
          },
          similarity_tag: {
            label: 'Card {{value}}%',
          },
        },
        lists: 'Lists',
        videostream: {
          removed: 'Videostream removed',
          unavailable: 'Videostream is unavailable',
        },
      },
      face_preview: {
        info: {
          created: 'Created',
          lists: 'Lists',
          face_id: 'Face ID',
          user_data: 'User data',
        },
      },
      error: {
        no_detections: 'No faces or silhouettes of bodies in the image',
      },
    },
    filters: {
      title: 'Search settings',
      buttons: {
        search_button: {
          label: 'Search',
        },
        reset_button: {
          label: 'Reset',
        },
        candidate_origin_button_group: {
          faces: 'Faces',
          events: 'Events',
        },
      },
      common: {
        from: 'From',
        to: 'To',
        similarity_percent: 'Similarity, %',
      },
      face_filters: {
        creation_date: 'Creation date',
        external_id: 'External ID',
        similarity: 'Similarity',
        lists: 'Lists',
        user_data: 'User data',
      },
      event_filters: {
        general: {
          title: 'General',
          creation_date: 'Event date',
          video_source: 'Source',
          handlers: 'Handling policies',
          similarity: 'Similarity',
        },
        comparing_attributes: {
          title: 'Labels',
          comparing_attribute: 'Comparing attribute',
          top_similar_objects: 'ID of objects with maximum match result',
        },
        face_attributes: {
          title: 'Face attributes and properties',
          gender: 'Gender',
          age: 'Age category',
          emotion: 'Emotion',
          mask: 'Mask',
          liveness: 'Liveness',
          deepfake: 'Deepfake',
          face_ids: 'Face IDs from events',
        },
        body_attributes: {
          title: 'Body attributes and properties',
          gender: 'Gender',
          age: 'Age category',
          upper_attributes: 'Upper body attributes',
          lower_attributes: 'Lower body attributes',
          shoes: 'Shoes',
          head_wear: 'Headwear',
          backpack: 'Backpack',
          upper_colors: 'Upper body colors',
          sleeve: 'Sleeve',
          lower_colors: 'Lower body colors',
          lower_type: 'Lower body type',
        },
        location: {
          title: 'Location',
          tabs: {
            map: 'Map',
            address: 'Address',
            coordinates: 'Coordinates',
          },
          address_tab: {
            city: 'City',
            area: 'Area',
            district: 'District',
            street: 'Street',
            house_number: 'House number',
          },
          coordinates_tab: {
            longitude: 'Longitude (-180...180)',
            latitude: 'Latitude (-90...90)',
            error: 'Error (0...90)',
          },
        },
      },
    },
    results: {
      title: 'Found',
      results_count: {
        label_events_one: '{{count}} event',
        label_events_other: '{{count}} events',
        label_faces_one: '{{count}} face',
        label_faces_other: '{{count}} faces',
      },
      sort_button: {
        label_similarity: 'By similarity',
        label_create_time_asc: 'By creation date (old first)',
        label_create_time_desc: 'By creation date (new first)',
      },
      no_data: 'No data',
    },
  },
}) : {}) as Record<string, unknown>;

export const esEs = (__LOCALE_ESES__ ? ({
  [ns]: {
    page: {
      title: 'Búsqueda',
      hint: {
        choose_search_object: 'Debe seleccionar un objeto de búsqueda.',
        choose_list: 'Debe seleccionar una lista.',
      },
      error: {
        search_failed: 'Búsqueda fallida',
      },
    },
    reference_selector: {
      title: 'Buscar por',
      search_type_switch: {
        buttons: {
          photo: 'Foto',
          event: 'ID del evento',
          face: 'ID del rostro',
        },
      },
      event_id_input: {
        placeholder: 'Introduzca el ID del evento',
      },
      face_input: {
        title: '¿Qué ID utiliza?',
        labels: {
          external_id: 'ID de tarjeta',
          luna_id: 'ID del rostro',
        },
        external_id_input: {
          placeholder: 'Introduzca <ID de tarjeta>',
        },
        face_id_input: {
          placeholder: 'Introduzca <ID del rostro>',
        },
        results: {
          faces: {
            title: 'Rostros',
          },
          bodies: {
            title: 'Cuerpos',
          },
        },
      },
      image_input: {
        title: 'Detectado',
        loader: {
          label: 'Detección en curso..',
        },
        info: {
          label: 'Seleccione el rostro/cuerpo deseado para la búsqueda',
        },
      },
      event_preview: {
        top_section: {
          badge: {
            label: 'Coincidencia',
            label_detection: 'Detección',
          },
          similarity_tag: {
            label: 'Tarjeta {{value}}%',
          },
        },
        lists: 'Listas',
        videostream: {
          removed: 'Flujo de vídeo eliminado',
          unavailable: 'Flujo de vídeo no disponible',
        },
      },
      face_preview: {
        info: {
          created: 'Creado',
          lists: 'Listas',
          face_id: 'ID del rostro',
          user_data: 'Información',
        },
      },
      error: {
        no_detections: 'No se detectaron rostros ni siluetas de cuerpos',
      },
    },
    filters: {
      title: 'Condiciones de búsqueda',
      buttons: {
        search_button: {
          label: 'Buscar',
        },
        reset_button: {
          label: 'Limpiar',
        },
        candidate_origin_button_group: {
          faces: 'Busco rostros',
          events: 'Busco eventos',
        },
      },
      common: {
        from: 'Desde',
        to: 'Hasta',
        similarity_percent: 'Similitud, %',
      },
      face_filters: {
        creation_date: 'Fecha de creación',
        external_id: 'ID externo',
        similarity: 'Similitud',
        lists: 'Listas',
        user_data: 'Datos de usuario',
      },
      event_filters: {
        general: {
          title: 'Generales',
          creation_date: 'Fecha del evento',
          video_source: 'Fuente',
          handlers: 'Escenarios',
          similarity: 'Similitud',
        },
        comparing_attributes: {
          title: 'Parámetros de comparación',
          comparing_attribute: 'Parámetro de comparación',
          top_similar_objects: 'ID de objetos con el resultado máximo de comparación',
        },
        face_attributes: {
          title: 'Atributos y propiedades del rostro',
          gender: 'Género',
          age: 'Edad',
          emotion: 'Emoción',
          mask: 'Mascarilla',
          liveness: 'Liveness',
          deepfake: 'Deepfake',
          face_ids: 'ID de rostros de eventos',
        },
        body_attributes: {
          title: 'Atributos y propiedades del cuerpo',
          gender: 'Género',
          age: 'Edad',
          upper_attributes: 'Parte superior',
          lower_attributes: 'Parte inferior',
          shoes: 'Calzado',
          head_wear: 'Prenda de cabeza',
          backpack: 'Mochila',
          upper_colors: 'Color de la parte superior',
          sleeve: 'Manga',
          lower_colors: 'Color de la parte inferior',
          lower_type: 'Tipo de la parte inferior',
        },
        location: {
          title: 'Ubicación',
          tabs: {
            map: 'Mapa',
            address: 'Dirección',
            coordinates: 'Coordenadas',
          },
          address_tab: {
            city: 'Ciudad',
            area: 'Área',
            district: 'Distrito',
            street: 'Calle',
            house_number: 'Número de casa',
          },
          coordinates_tab: {
            longitude: 'Longitud (-180...180)',
            latitude: 'Latitud (-90...90)',
            error: 'Error (0...90)',
          },
        },
      },
    },
    results: {
      title: 'Encontrado',
      results_count: {
        label_events_one: '{{count}} evento',
        label_events_other: '{{count}} eventos',
        label_faces_one: '{{count}} rostro',
        label_faces_other: '{{count}} rostros',
      },
      sort_button: {
        label_similarity: 'Por similitud',
        label_create_time_asc: 'Por fecha de creación (primero los antiguos)',
        label_create_time_desc: 'Por fecha de creación (primero los nuevos)',
      },
      no_data: 'Sin datos',
    },
  },
}) : {}) as Record<string, unknown>;

export const ptBr = (__LOCALE_PTBR__ ? ({
  [ns]: {
    page: {
      title: 'Busca',
      hint: {
        choose_search_object: 'É necessário selecionar um objeto de busca.',
        choose_list: 'É necessário selecionar uma lista.',
      },
      error: {
        search_failed: 'Busca falhou',
      },
    },
    reference_selector: {
      title: 'Buscar por',
      search_type_switch: {
        buttons: {
          photo: 'Foto',
          event: 'ID do evento',
          face: 'ID do rosto',
        },
      },
      event_id_input: {
        placeholder: 'Insira o ID do evento',
      },
      face_input: {
        title: 'Qual ID você utiliza?',
        labels: {
          external_id: 'ID do cartão',
          luna_id: 'ID do rosto',
        },
        external_id_input: {
          placeholder: 'Insira <ID do cartão>',
        },
        face_id_input: {
          placeholder: 'Insira <ID do rosto>',
        },
        results: {
          faces: {
            title: 'Rostos',
          },
          bodies: {
            title: 'Corpos',
          },
        },
      },
      image_input: {
        title: 'Detectado',
        loader: {
          label: 'Detecção em andamento..',
        },
        info: {
          label: 'Selecione o rosto/corpo desejado para a busca',
        },
      },
      event_preview: {
        top_section: {
          badge: {
            label: 'Correspondência',
            label_detection: 'Detecção',
          },
          similarity_tag: {
            label: 'Cartão {{value}}%',
          },
        },
        lists: 'Listas',
        videostream: {
          removed: 'Fluxo de vídeo removido',
          unavailable: 'Fluxo de vídeo indisponível',
        },
      },
      face_preview: {
        info: {
          created: 'Criado',
          lists: 'Listas',
          face_id: 'ID do rosto',
          user_data: 'Informações',
        },
      },
      error: {
        no_detections: 'Não foram detectados rostos nem silhuetas de corpos',
      },
    },
    filters: {
      title: 'Condições de busca',
      buttons: {
        search_button: {
          label: 'Buscar',
        },
        reset_button: {
          label: 'Limpar',
        },
        candidate_origin_button_group: {
          faces: 'Busco rostos',
          events: 'Busco eventos',
        },
      },
      common: {
        from: 'De',
        to: 'Até',
        similarity_percent: 'Similaridade, %',
      },
      face_filters: {
        creation_date: 'Data de criação',
        external_id: 'ID externo',
        similarity: 'Similaridade',
        lists: 'Listas',
        user_data: 'Dados do usuário',
      },
      event_filters: {
        general: {
          title: 'Gerais',
          creation_date: 'Data do evento',
          video_source: 'Fonte',
          handlers: 'Cenários',
          similarity: 'Similaridade',
        },
        comparing_attributes: {
          title: 'Parâmetros de comparação',
          comparing_attribute: 'Parâmetro de comparação',
          top_similar_objects: 'ID de objetos com o resultado máximo de comparação',
        },
        face_attributes: {
          title: 'Atributos e propriedades do rosto',
          gender: 'Gênero',
          age: 'Idade',
          emotion: 'Emoção',
          mask: 'Máscara',
          liveness: 'Liveness',
          deepfake: 'Deepfake',
          face_ids: 'ID de rostos de eventos',
        },
        body_attributes: {
          title: 'Atributos e propriedades do corpo',
          gender: 'Gênero',
          age: 'Idade',
          upper_attributes: 'Parte superior',
          lower_attributes: 'Parte inferior',
          shoes: 'Calçado',
          head_wear: 'Peça de cabeça',
          backpack: 'Mochila',
          upper_colors: 'Cor da parte superior',
          sleeve: 'Manga',
          lower_colors: 'Cor da parte inferior',
          lower_type: 'Tipo da parte inferior',
        },
        location: {
          title: 'Localização',
          tabs: {
            map: 'Mapa',
            address: 'Endereço',
            coordinates: 'Coordenadas',
          },
          address_tab: {
            city: 'Cidade',
            area: 'Área',
            district: 'Distrito',
            street: 'Rua',
            house_number: 'Número da casa',
          },
          coordinates_tab: {
            longitude: 'Longitude (-180...180)',
            latitude: 'Latitude (-90...90)',
            error: 'Erro (0...90)',
          },
        },
      },
    },
    results: {
      title: 'Encontrado',
      results_count: {
        label_events_one: '{{count}} evento',
        label_events_other: '{{count}} eventos',
        label_faces_one: '{{count}} rosto',
        label_faces_other: '{{count}} rostos',
      },
      sort_button: {
        label_similarity: 'Por similaridade',
        label_create_time_asc: 'Por data de criação (primeiro os antigos)',
        label_create_time_desc: 'Por data de criação (primeiro os novos)',
      },
      no_data: 'Sem dados',
    },
  },
}) : {}) as Record<string, unknown>;
